import { memo, useMemo, useState, useEffect } from 'react';
import { Navigate, useLocation, matchPath, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Placeholder from '../pages/Placeholder';
import Spinner from '../components/reusable/spinner/Spinner';
import DownloadAppBanner from '../components/reusable/DownloadAppBanner';
import { getSingleModel, isUuid } from '../utils/helpers';
import useViewProfileToken, {
    VIEW_PROFILE_STATE
} from './hooks/useViewProfileToken';

const TITLE_BY_PATH = {
    '/profile': 'profile',
    '/user/announcement': 'announcement'
};

const PLACEHOLDER_ROUTES = [
    '/user/announcement',
    '/profile',
    'user/comment',
    '/feed/share'
];

// Context passed to predicates
const buildCtx = ({
    userType,
    idParam,
    location,
    search,
    params,
    viewProfileToken
}) => ({
    userType,
    idParam,
    search,
    pathname: location.pathname,
    location,
    params,
    viewProfileToken
});

// Each rule: { pattern, canBypass: (ctx, match) => boolean }
// ======================================
const ROUTE_BYPASS_RULES = [
    {
        pattern: '/profile',
        canBypass: (ctx, _) =>
            ['PLAYER', 'ADMIN'].includes(ctx.userType) &&
            isUuid(ctx.idParam) &&
            ctx.viewProfileToken === VIEW_PROFILE_STATE.VALID
    },
    {
        pattern: 'user/comment',
        canBypass: (ctx, _) =>
            Boolean(
                ctx.search.get('highlightId')?.length > 8 &&
                    ctx.search.get('userId')?.length > 8
            )
    },
    {
        pattern: '/feed/share',
        canBypass: ctx => Object.keys(ctx.location.state).length > 0 // check if state is passed
    },
    {
        pattern: '/reference/:toUserId',
        canBypass: ctx =>
            ctx.userType === 'PLAYER' && isUuid(ctx.params?.toUserId)
    }
];
// ======================================

const evaluateRouteSpecificBypass = (pathname, ctx) => {
    for (const rule of ROUTE_BYPASS_RULES) {
        const matchResult =
            matchPath({ path: rule.pattern, end: true }, pathname) ||
            matchPath({ path: rule.pattern, end: false }, pathname);

        if (matchResult) {
            // If the rule matches the path, enforce its custom predicate
            const result = Boolean(rule.canBypass(ctx, matchResult));

            return result;
        }
    }
    return false;
};

const PrivateRouteWithPlaceholder = memo(({ element }) => {
    const location = useLocation();
    const search = new URLSearchParams(location.search);
    const idParam = search.get('id');
    const params = useParams();

    const user = useSelector(state => state.user?.guest);
    const userLoaded = useSelector(state => state.user?.loaded);

    const authModel = getSingleModel('auth');
    const isAuthReady = authModel !== undefined && authModel !== null;
    const isAuthenticated = Boolean(authModel?.authUser?.authenticated);

    // Timeout mechanism to prevent infinite loading
    const [loadingTimeout, setLoadingTimeout] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) {
            setLoadingTimeout(true);
            return undefined;
        }

        // Set a timeout to force resolution after 10 seconds
        const timer = setTimeout(() => {
            setLoadingTimeout(true);
        }, 10000);

        return () => clearTimeout(timer);
    }, [location.pathname, isAuthenticated]);

    const pageTitle = useMemo(
        () => TITLE_BY_PATH[location.pathname] ?? 'announcement',
        [location.pathname]
    );

    const viewProfileToken = useViewProfileToken(
        !isAuthenticated ? search.get('shareToken') : null
    );
    const ctx = useMemo(
        () =>
            buildCtx({
                userType: user?.userType,
                idParam,
                location,
                search,
                params,
                viewProfileToken
            }),
        [user, idParam, location, search, params, viewProfileToken]
    );

    // 1) Route-specific bypass (e.g., /profile => PLAYER + UUID)
    // Wait for user data to be fully loaded before evaluating bypass
    const canBypassAuth = evaluateRouteSpecificBypass(location.pathname, ctx);

    if (canBypassAuth) {
        return (
            <>
                <DownloadAppBanner />
                <div className='mt-[80px]'>{element}</div>
            </>
        );
    }

    const isProfileRoute = location.pathname === '/profile';

    // Wait for both auth and user data to be ready, but respect timeout
    const shouldShowLoading =
        !loadingTimeout &&
        (!isAuthReady ||
            (isProfileRoute && !userLoaded) ||
            (isProfileRoute &&
                viewProfileToken === VIEW_PROFILE_STATE.LOADING));

    if (shouldShowLoading) {
        return (
            <div className='w-full justify-center flex h-screen'>
                <Spinner />
            </div>
        );
    }

    // Normal: authenticated users proceed
    if (isAuthenticated) {
        return (
            <>
                <DownloadAppBanner />
                <div className='mt-[80px]'>{element}</div>
            </>
        );
    }

    if (PLACEHOLDER_ROUTES.includes(location.pathname)) {
        return <Placeholder pageTitle={pageTitle} />;
    }

    return <Navigate to='/login' state={{ from: location }} replace />;
});

export default PrivateRouteWithPlaceholder;
